// The licence tier and its modules on the licence page (08.10.2026,
// anima_core #9, anima_frontend #2). Pure, no imports: the node tests load
// this file directly (tier.test.ts).
//
// Which modules a tier includes is the hub's knowledge (anima_core
// Helper/LicenceTiers.cs); the page gets it with the licence details as
// tierFeatures and keeps no list of its own.

/** The three licence tiers (28.09.2026); each contains the one below. */
export const licenceTiers = ["free", "business", "enterprise"] as const;
export type LicenceTier = typeof licenceTiers[number];

/** Every tier by name with the modules it brings by itself, as the hub sends it. */
export type TierFeatures = Readonly<Record<string, readonly string[]>>;

/**
 * The tier from what the hub reports, old edition names included (Basic,
 * Professional, Security). Null when nothing usable came - an older hub sent
 * no tier at all, and reading that as Free made "Save tier" a downgrade.
 */
export function tierOf(edition: unknown): LicenceTier | null {
    const e = typeof edition === "string" ? edition.trim().toLowerCase() : "";
    if (e === "free" || e === "basic") return "free";
    if (e === "business" || e === "professional") return "business";
    if (e === "enterprise" || e === "security") return "enterprise";
    return null;
}

/** The tier's name for display: "Business". */
export const tierLabel = (tier: LicenceTier): string => tier.charAt(0).toUpperCase() + tier.slice(1);

/**
 * Whether saving the tier would change anything: a tier is chosen and it is
 * not the one the hub reported. Saving is offered only then.
 */
export const tierChanged = (stored: unknown, selected: unknown): boolean => {
    const chosen = tierOf(selected);
    return chosen !== null && chosen !== tierOf(stored);
};

const lower = (name: string): string => name.trim().toLowerCase();

/**
 * The modules a tier includes, lower case, from the hub's tierFeatures. Empty
 * for an unknown tier or when the hub sent no list (an older hub): then
 * nothing is shown as included, and every module stays selectable.
 */
export function includedModules(features: TierFeatures | null | undefined, tier: LicenceTier | null): string[] {
    if (tier === null || !features) return [];
    const key = Object.keys(features).find((k) => tierOf(k) === tier);
    const list = key === undefined ? undefined : features[key];
    return Array.isArray(list) ? list.filter((n): n is string => typeof n === "string").map(lower) : [];
}

/** Whether a module comes with the tier (names compared ignoring case). */
export const isIncluded = (name: string, included: readonly string[]): boolean =>
    included.includes(lower(name));

/**
 * What the checkbox group shows ticked: the extras on the licence and
 * everything the tier includes. Catalog names are kept as the catalog spells
 * them, so a ticked box matches its value.
 */
export function shownTicked(extras: readonly string[], included: readonly string[], catalog: readonly string[]): string[] {
    return catalog.filter((name) => isIncluded(name, included) || extras.some((e) => lower(e) === lower(name)));
}

/**
 * The extras to keep after the user changed the checkbox group. Included
 * boxes are disabled, so their state says nothing about the user's wish:
 * an extra stored on the licence that the tier happens to include stays as it
 * is (it matters again if the tier goes down), and an included one is never
 * added. Everything else follows the selection.
 */
export function extrasAfterChange(selection: readonly string[], included: readonly string[], previous: readonly string[]): string[] {
    const kept = previous.filter((name) => isIncluded(name, included));
    const chosen = selection.filter((name) => !isIncluded(name, included));
    return [...kept, ...chosen].filter((name, i, all) => all.findIndex((n) => lower(n) === lower(name)) === i);
}
